import React, { useState, useEffect } from 'react';
import { supabase, handleSupabaseError, OperationType } from '../supabase';
import { CryptoEvent, UserProfile, EventStatus, CATEGORY_BY_VALUE, rowToEvent } from '../types';
import { format } from 'date-fns';
import {
  Calendar as CalendarIcon,
  Edit2,
  Trash2,
  Clock,
  CheckCircle2,
  AlertCircle,
  Search,
  Filter,
  Info,
} from 'lucide-react';
import { AddEventModal } from './AddEventModal';

interface MyEventsProps {
  userProfile: UserProfile;
  isAdmin: boolean;
}

const statusBadge = (status: EventStatus) => {
  if (status === 'approved')
    return (
      <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full border-[1.5px] border-[#0b0b0c] bg-[#14f195] text-[#0b0b0c] text-[10px] font-bold uppercase tracking-wider">
        <CheckCircle2 className="w-3 h-3" />
        Approved
      </span>
    );
  if (status === 'pending')
    return (
      <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full border-[1.5px] border-[#0b0b0c] bg-[#ffd24a] text-[#0b0b0c] text-[10px] font-bold uppercase tracking-wider">
        <Clock className="w-3 h-3" />
        Pending
      </span>
    );
  if (status === 'rejected')
    return (
      <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full border-[1.5px] border-[#0b0b0c] bg-[#ff6a6a] text-white text-[10px] font-bold uppercase tracking-wider">
        <AlertCircle className="w-3 h-3" />
        Rejected
      </span>
    );
  return null;
};

export const MyEvents: React.FC<MyEventsProps> = ({ userProfile, isAdmin }) => {
  const [events, setEvents] = useState<CryptoEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<EventStatus | 'all'>('all');
  const [editingEvent, setEditingEvent] = useState<CryptoEvent | null>(null);
  const [deletingEventId, setDeletingEventId] = useState<string | null>(null);
  const [submittingChange, setSubmittingChange] = useState(false);
  const [notification, setNotification] =
    useState<{ type: 'success' | 'error'; message: string } | null>(null);

  useEffect(() => {
    let channel: ReturnType<typeof supabase.channel> | null = null;
    let cancelled = false;

    const fetchMine = async () => {
      const { data: { user: authUser } } = await supabase.auth.getUser();
      if (!authUser) return;
      const { data, error } = await supabase
        .from('events')
        .select('*')
        .eq('created_by', authUser.id)
        .order('starts_at', { ascending: false });
      if (cancelled) return;
      if (error) {
        handleSupabaseError(error, OperationType.SELECT, 'events');
        setLoading(false);
        return;
      }
      setEvents((data ?? []).map(rowToEvent));
      setLoading(false);

      if (!channel) {
        channel = supabase
          .channel(`events:my:${authUser.id}`)
          .on(
            'postgres_changes',
            { event: '*', schema: 'public', table: 'events', filter: `created_by=eq.${authUser.id}` },
            () => void fetchMine(),
          )
          .subscribe();
      }
    };

    void fetchMine();
    return () => {
      cancelled = true;
      if (channel) supabase.removeChannel(channel);
    };
  }, []);

  const handleDelete = async (eventId: string) => {
    setSubmittingChange(true);
    try {
      const { error } = await supabase.from('events').delete().eq('id', eventId);
      if (error) throw error;
      setNotification({ type: 'success', message: 'Event deleted successfully.' });
      setDeletingEventId(null);
    } catch (error) {
      setNotification({ type: 'error', message: 'Failed to delete event.' });
      handleSupabaseError(error, OperationType.DELETE, 'events');
    } finally {
      setSubmittingChange(false);
    }
  };

  const filteredEvents = events.filter((event) => {
    const search = searchQuery.toLowerCase();
    const matchesSearch =
      event.title.toLowerCase().includes(search) ||
      (event.description?.toLowerCase().includes(search) ?? false);
    const matchesStatus = statusFilter === 'all' || event.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="w-8 h-8 border-3 border-[#9945ff] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      <div className="sticker bg-white p-4 sm:p-5">
        <p className="font-bold text-[10px] uppercase tracking-wider text-[#0b0b0c]/55">
          Your submissions
        </p>
        <h2 className="font-bold text-2xl sm:text-3xl uppercase tracking-tight text-[#0b0b0c]">My events</h2>
        <p className="text-sm text-[#0b0b0c]/65 mt-1">Manage and track your event submissions.</p>
      </div>

      {notification && (
        <div
          className={`sticker-soft p-3 flex items-center justify-between gap-3 ${
            notification.type === 'success' ? 'bg-[#14f195]' : 'bg-[#ff6a6a] text-white'
          }`}
        >
          <div className="flex items-center gap-2">
            {notification.type === 'success' ? (
              <CheckCircle2 className="w-4 h-4 text-[#0b0b0c]" />
            ) : (
              <AlertCircle className="w-4 h-4 text-white" />
            )}
            <span className="text-sm font-bold">{notification.message}</span>
          </div>
          <button
            onClick={() => setNotification(null)}
            className="min-h-11 px-2 text-xs font-bold uppercase tracking-wider hover:underline"
          >
            Dismiss
          </button>
        </div>
      )}

      <div className="flex flex-col md:flex-row gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-[#0b0b0c]/45" />
          <input
            type="text"
            placeholder="Search my events..."
            className="w-full h-11 pl-10 pr-4 rounded-full border-[1.5px] border-[#0b0b0c] bg-white text-sm font-medium text-[#0b0b0c] placeholder:text-[#0b0b0c]/40 focus:outline-none focus:ring-2 focus:ring-[#9945ff]/30"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
        <div className="relative md:w-56 shrink-0">
          <Filter className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-[#0b0b0c]/45" />
          <select
            className="w-full h-11 pl-10 pr-4 rounded-full border-[1.5px] border-[#0b0b0c] bg-white text-sm font-bold focus:outline-none appearance-none"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as any)}
          >
            <option value="all">All status</option>
            <option value="pending">Pending</option>
            <option value="approved">Approved</option>
            <option value="rejected">Rejected</option>
          </select>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-3">
        {filteredEvents.length === 0 ? (
          <div className="sticker bg-[#e9dffa] text-center py-16">
            <div className="w-16 h-16 sticker-soft bg-white inline-flex items-center justify-center mb-4">
              <CalendarIcon className="w-7 h-7 text-[#0b0b0c]/55" />
            </div>
            <h3 className="font-bold text-2xl uppercase tracking-tight text-[#0b0b0c] mb-1">
              No events found
            </h3>
            <p className="text-sm text-[#0b0b0c]/65">
              {events.length === 0
                ? "You haven't submitted any events yet."
                : 'No events match your current filters.'}
            </p>
          </div>
        ) : (
          filteredEvents.map((event) => {
            const cat = CATEGORY_BY_VALUE[event.category];
            return (
              <div key={event.id} className="sticker bg-white p-4">
                <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
                  <div className="flex items-start sm:items-center gap-3 sm:gap-4 flex-1 min-w-0">
                    <div className="sticker-soft bg-[#f3ecfc] w-14 h-14 flex flex-col items-center justify-center shrink-0">
                      <span className="font-bold text-[9px] uppercase tracking-wider text-[#0b0b0c]/55">
                        {format(event.date, 'MMM').toUpperCase()}
                      </span>
                      <span className="font-bold text-2xl text-[#0b0b0c] leading-none">
                        {format(event.date, 'dd')}
                      </span>
                    </div>
                    <div className="space-y-2 flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        {statusBadge(event.status)}
                        {cat && (
                          <span
                            className="px-2 py-0.5 rounded-full border-[1.5px] border-[#0b0b0c] text-[10px] font-bold uppercase tracking-wider"
                            style={{
                              backgroundColor: cat.fill,
                              color: cat.textColor === 'white' ? '#fff' : '#0b0b0c',
                            }}
                          >
                            {cat.label}
                          </span>
                        )}
                        {event.chain && (
                          <span className="px-2 py-0.5 rounded-full pill-outline text-[10px] font-bold uppercase tracking-wider">
                            {event.chain === 'General Crypto' ? 'Industry-wide' : event.chain}
                          </span>
                        )}
                      </div>
                      <h3 className="font-bold text-lg uppercase tracking-tight text-[#0b0b0c] leading-tight">
                        {event.title}
                      </h3>
                      {event.status === 'rejected' && event.rejectionReason && (
                        <div className="sticker-soft bg-[#f3ecfc] p-3 flex items-start gap-2 max-w-md mt-2">
                          <Info className="w-3.5 h-3.5 text-[#0b0b0c]/55 shrink-0 mt-0.5" />
                          <div>
                            <p className="font-bold text-[9px] uppercase tracking-wider text-[#0b0b0c]/55">
                              Admin feedback
                            </p>
                            <p className="text-[12px] text-[#0b0b0c]/75 italic">
                              "{event.rejectionReason}"
                            </p>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0 w-full md:w-auto">
                    <button
                      onClick={() => setEditingEvent(event)}
                      className="inline-flex flex-1 md:flex-none items-center justify-center gap-1.5 px-3 h-11 sm:h-9 rounded-full border-[1.5px] border-[#0b0b0c] bg-white text-[#0b0b0c] text-[11px] font-bold uppercase tracking-wider hover:bg-[#0b0b0c] hover:text-white transition-colors"
                    >
                      <Edit2 className="w-3.5 h-3.5" />
                      Edit
                    </button>
                    <button
                      onClick={() => setDeletingEventId(event.id)}
                      className="inline-flex flex-1 md:flex-none items-center justify-center gap-1.5 px-3 h-11 sm:h-9 rounded-full border-[1.5px] border-[#0b0b0c] bg-[#ff6b4d] text-white text-[11px] font-bold uppercase tracking-wider hover:bg-[#ff6a6a] transition-colors"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                      Delete
                    </button>
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>

      {deletingEventId && (
        <div className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center safe-overlay bg-[#0b0b0c]/40">
          <div className="sticker bg-[#e9dffa] max-w-sm w-full p-6 text-center">
            <div className="w-14 h-14 sticker-soft bg-[#ff6b4d] inline-flex items-center justify-center mb-4">
              <Trash2 className="w-7 h-7 text-white" />
            </div>
            <h3 className="font-bold text-2xl uppercase tracking-tight text-[#0b0b0c] mb-1">
              Delete event?
            </h3>
            <p className="text-sm text-[#0b0b0c]/65 mb-5">
              This action cannot be undone. The event will be permanently removed.
            </p>
            <div className="flex flex-col sm:flex-row gap-2">
              <button
                disabled={submittingChange}
                onClick={() => setDeletingEventId(null)}
                className="flex-1 h-11 rounded-full bg-white text-[#0b0b0c] border-[1.5px] border-[#0b0b0c] font-bold text-sm uppercase tracking-wider hover:bg-[#0b0b0c] hover:text-white transition-colors"
              >
                Cancel
              </button>
              <button
                disabled={submittingChange}
                onClick={() => handleDelete(deletingEventId)}
                className="flex-1 h-11 rounded-full bg-[#ff6b4d] text-white border-[1.5px] border-[#0b0b0c] font-bold text-sm uppercase tracking-wider hover:bg-[#ff6a6a] transition-colors disabled:opacity-50"
              >
                {submittingChange ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}

      {editingEvent && (
        <AddEventModal
          onClose={() => setEditingEvent(null)}
          onSuccess={(message) => {
            setEditingEvent(null);
            setNotification({ type: 'success', message });
          }}
          isAdmin={isAdmin}
          userProfile={userProfile}
          editEvent={editingEvent}
        />
      )}
    </div>
  );
};
